import { HttpClient } from '@angular/common/http';
import { effect, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { BaseService } from '../../core/services/base.service';
import { TranslationService } from '../../core/services/translation.service';
import { Searchable } from '../interfaces/searchable.model';
import { ISelectOption } from '../interfaces/select-option.interface';
import { BaseColorModel } from '../models/base-color.model';

@Injectable({
  providedIn: 'root',
})
export class BaseColorService extends BaseService {
  private baseColors$ = new BehaviorSubject<Searchable<BaseColorModel>>(
    new Searchable<BaseColorModel>()
  );

  constructor(
    http: HttpClient,
    private readonly i18n: TranslationService
  ) {
    super(http, 'Base color', '/base-color', BaseColorModel);

    // Base-color names are translatable but the option list is cached in
    // `baseColors$`. When the language switches, re-fetch (only if already
    // loaded) so every subscribed select re-renders with translated names.
    effect(() => {
      this.i18n.language();
      if (this.baseColors$.value.options.length > 0) {
        this.getAllForSelect().subscribe();
      }
    });
  }

  getBaseColors(): Observable<Searchable<BaseColorModel>> {
    return this.baseColors$.asObservable();
  }

  getAllForSelect(): Observable<Searchable<BaseColorModel>> {
    return this.http
      .get<Searchable<BaseColorModel>>(`${this.uri}/selectAll`, {
        headers: this.defaultHeaders,
        params: this.withLang(),
      })
      .pipe(
        map((response: any) => {
          const data = response.data.map((row: any) => new this.Model(row));
          const select = data.map((row: any) => row?.toSelect() || row);
          return { ...response, data, select };
        }),
        tap((response: any) => {
          const baseColorsResp = new Searchable<BaseColorModel>();
          baseColorsResp.update(response, 1);
          return this.baseColors$.next(baseColorsResp);
        })
      );
  }

  getBaseColorsForSelect(search?: string): Observable<Searchable<BaseColorModel>> {
    return this.getBaseColors().pipe(
      map((searchable) => {
        if (!search) {
          return searchable;
        }

        const filtered = searchable.options.filter((option: ISelectOption) => {
          return option.label.toLowerCase().includes(search.toLowerCase());
        });

        const result = new Searchable<BaseColorModel>();
        result.update(filtered, 1);
        return result;
      })
    );
  }
}
